import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { Link } from 'react-router-dom'
import Card from '../../../../molecules/Card'
import {
  useCheckMigrationsMutation,
  useListIntegrationDomains,
  useListIntegrationSources,
  useScanMutation,
  useSyncMutation
} from '../../../../../api/integrations'
import AdminPage from '../../../admin/shared/AdminPage'
import Loading from '../../../../molecules/Loading'
import { useDenaliContext } from '../../DenaliProvider'
import { SUCCESS_ALERT } from '../../../../../constants'
import NavTile from '../../shared/NavTile'
import SydActionMenu from '../../../../commonDesign/SydActionMenu'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import IntegrationSourceList from './IntegrationSourceList'

const useIntegrationActions = (root) => {
  const denaliContext = useDenaliContext()
  const { mutateAsync: syncJob } = useSyncMutation()
  const { mutateAsync: scanJob } = useScanMutation()
  const { mutateAsync: migrate } = useCheckMigrationsMutation()

  const handleSync = useCallback(async (domain) => {
    const result = await syncJob({
      domain: domain.domain
    })

    denaliContext.setAlert({
      ...SUCCESS_ALERT,
      alertMessage: (
        <Link to={`${root}/integrator/batches/${result.batchId}`}>Sync started in batch {result.batchId}</Link>
      )
    })
    return result
  }, [syncJob, denaliContext, root])

  const handleScan = useCallback(async (domain) => {
    const result = await scanJob({
      domain: domain.domain
    })

    denaliContext.setAlert({
      ...SUCCESS_ALERT,
      alertMessage: (
        <Link to={`${root}/integrator/batches/${result.batchId}`}>Scan started in batch {result.batchId}</Link>
      )
    })
    return result
  }, [scanJob, denaliContext, root])

  const handleMigrate = useCallback(async (domain) => {
    const result = await migrate({
      domain: domain.domain
    })

    denaliContext.setAlert({
      ...SUCCESS_ALERT,
      alertMessage: (
        <Link to={`${root}/integrator/batches/${result.batchId}`}>Migration Check started in batch {result.batchId}</Link>
      )
    })
    return result
  }, [migrate, denaliContext, root])

  return useMemo(() => {
    return [
      { key: 'handle-domain-scan', label: 'Scan', icon: 'search', onClick: handleScan },
      { key: 'handle-domain-sync', label: 'Sync', icon: 'sync', onClick: handleSync },
      { key: 'handle-domain-migrate', label: 'Check Migrations', icon: 'database', onClick: handleMigrate }
    ]
  }, [handleSync, handleScan, handleMigrate])
}

function IntegrationDomains ({ root }) {
  const { data, isLoading } = useListIntegrationDomains()
  const { data: isData, isLoading: integrationSourcesLoading } = useListIntegrationSources()
  const domainActions = useIntegrationActions(root)
  const [selectedDomain, setSelectedDomain] = useState(null)

  useEffect(() => {
    if (data?.integrationDomains?.length && !selectedDomain) {
      setSelectedDomain(data.integrationDomains.at(0))
    }
  }, [data, selectedDomain, setSelectedDomain])

  if (isLoading || integrationSourcesLoading) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          {(data?.integrationDomains || []).map(domain => (
            <NavTile
              key={`domain_${domain.domain}`}
              label={domain.domain}
              menu={<SydActionMenu actions={domainActions} subject={domain} />}
              selected={selectedDomain === domain}
              onSelect={() => setSelectedDomain(domain)}
            />
          ))}
        </Grid>
        <Grid item xs={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {selectedDomain ? (
                <FadeIn>
                  <Card>
                    <IntegrationSourceList
                      root={root}
                      domain={selectedDomain.domain}
                      sources={(isData?.integrationSources || [])}
                    />
                  </Card>
                </FadeIn>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AdminPage>
  )
}

IntegrationDomains.propTypes = {
  root: PropTypes.any
}

export default IntegrationDomains
